// pages/Quiz1Edit.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {getQuizById, updateQuiz, uploadPatQuizImage, uploadQuizImage} from '../services/api';
import {Box, Button, AppBar, Toolbar, Grid, Typography, Modal, LinearProgress, CircularProgress, TextField, Tabs, Tab} from '@mui/material';
import {QuizModel} from "../services/types";
import {Add, Folder, Delete, CloudUpload, ArrowBack} from '@mui/icons-material';
import QuestionEditor from '../components/QuestionEditor'
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-dark.css'
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import ReactQuillEditor from "../components/ReactQuillEditor";
import MediaBlockEditor from "../components/MediaBlockEditor";
import EditorToggle from "../components/EditorToggle";


interface UploadAreaProps {
    onUpload: (file: File) => void;
}
interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    progress: number;
}

const Quiz1Edit: React.FC = () => {
    const { quizId } = useParams();
    const [loading, setLoading] = useState(true);
    const [quiz, setQuiz] = useState<QuizModel>();
    const [editorData, setEditorData] = React.useState('');

    const [activeTab, setActiveTab] = useState('patient');
    const [isActive, setIsActive] = useState(true);
    const [savingProcess, setSavingProcess] = useState(false);
    const [content, setContent] = useState('');
    const [patientContent, setPatientContent] = useState('');
    const [resultContent, setResultContent] = useState('');
    useEffect(() => {
        setLoading(true)
        const fetchQuiz = async () => {
            if (!quizId) return;

            try {
                const response = await getQuizById(quizId);
                setQuiz(response);

                setContent(response?.description)
                setPatientContent(response?.diagnosis)
                setResultContent(response?.result_text)
                setIsActive(response?.is_active)

                setLoading(false)
            } catch (error) {
                console.error('Error fetching quiz:', error);
            }
        };
        fetchQuiz();
    }, [quizId]);

    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline'],
            ['link', 'image']
        ],
        clipboard: {
            matchVisual: false,
        }
    };

    const handleSave = async () => {
        if (!quizId) return;

        setSavingProcess(true)

        try {
            await updateQuiz(quizId, quiz);
            console.log('Quiz saved successfully');
            setSavingProcess(false)

        } catch (error) {
            console.error('Error saving quiz:', error);
            setSavingProcess(false)
        }
    };

    const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return; // Проверка на null

        const file = files[0];
        if (!quizId) return; // Проверка на undefined

        uploadQuizImage(quizId, file)
            .then((response) => {
                setQuiz({ ...quiz!, image: response.image });
            })
            .catch((error) => console.error('Error uploading quiz image:', error));
    };
    const handlePatPhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files) return; // Проверка на null

        const file = files[0];
        if (!quizId) return; // Проверка на undefined

        uploadPatQuizImage(quizId, file)
            .then((response) => {
                setQuiz({ ...quiz!, patient_photo: response.image });
            })
            .catch((error) => console.error('Error uploading quiz image:', error));
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
    };

    const handleContentChange = (value:string) => {
        setContent(value);
        setQuiz({...quiz!, description: value})
    };
    const handlePatientChange = (value:string) => {
        setPatientContent(value);
        setQuiz({...quiz!, diagnosis: value})
    };
    const handleResultChange = (value:string) => {
        setResultContent(value);
        setQuiz({...quiz!, result_text: value})
    };

    const handleActiveChange = (value:boolean) => {
        setIsActive(value);
        setQuiz({...quiz!, is_active: value})
    };


    return (
        <Box>
            <AppBar position="static">
                <Toolbar className='toolbar'>
                    <Box className='toolbarTitle'>
                        <Box>{quiz?.title}</Box>
                    </Box>
                </Toolbar>
            </AppBar>

            {!loading ?
                <>
                    <Box className='topButtonsContainer hideMPageButtons'>
                        <Typography variant="h6">Основные данные</Typography>
                        <Button className='topButtonsAdd' variant="outlined" onClick={() => handleSave()}>
                            {savingProcess && (<CircularProgress/> )}
                            Сохранить
                        </Button>
                    </Box>

                    <Box className="conentContainer" sx={{display: 'flex', flexDirection: 'column'}}>
                        <Box sx={{mb: 2}}>
                            <label>
                                <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => handleActiveChange(e.target.checked)}
                                />
                                Активный
                            </label>
                        </Box>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Название"
                            type="text"
                            value={quiz?.title}
                            sx={{width: '1000px'}}
                            onChange={(e) => setQuiz({...quiz!, title: e.target.value})}
                        />

                        <EditorToggle
                            width="1000px"
                            label="Описание"
                            initialValue={content}
                            onChange={handleContentChange}
                        />

                        <Box sx={{display: 'flex', gap: '10px', mt: 2}}>
                            {quiz?.image !== null && (
                                <img src={quiz?.image} alt={quiz?.title} width='400px'/>
                            )}
                            <input
                                accept="image/*"
                                type="file"
                                onChange={handlePhotoUpload}
                                style={{display: 'none'}}
                                id="avatar-upload"
                            />
                            <label htmlFor="avatar-upload">
                                <Button variant="contained" className='profileBtn' component="span"
                                        sx={{mb: 2, background: '#3a485b'}}>
                                    Загрузить фото
                                </Button>
                            </label>
                        </Box>


                    </Box>
                    <Box className="conentContainer">
                        <Box sx={{mt: 5, mb: 4}}>
                            <Tabs value={activeTab} onChange={handleTabChange} sx={{backgroundColor:'#50329130'}}>
                                <Tab value="patient" label="Пациент" />
                                <Tab value="complaints" label={quiz?.complaint_text=="" ? "Раздел 1" : quiz?.complaint_text} />
                                <Tab value="anamnesis" label={quiz?.anamnesis_text=="" ? "Раздел 2" : quiz?.anamnesis_text} />
                                <Tab value="preliminaryDiagnosis" label={quiz?.preliminary_diagnosis_text=="" ? "Раздел 3" : quiz?.preliminary_diagnosis_text} />
                                <Tab value="research" label={quiz?.research_text=="" ? "Раздел 4" : quiz?.research_text} />
                                <Tab value="finalDiagnosis" label={quiz?.final_diagnosis_text=="" ? "Раздел 5" : quiz?.final_diagnosis_text} />
                                <Tab value="treatment" label={quiz?.treatment_text=="" ? "Раздел 6" : quiz?.treatment_text} />
                                <Tab value="conclusions" label={quiz?.conclusions_text=="" ? "Раздел 7" : quiz?.conclusions_text} />
                                <Tab value="result" label="Результаты" />
                            </Tabs>


                            {activeTab === 'patient' && (
                                <Box sx={{display: 'flex', flexDirection: 'column', mt: 2}}>


                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Имя пациента"
                                        type="text"
                                        value={quiz?.patient_name}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, patient_name: e.target.value})}
                                    />
                                    <br/>
                                    <Box sx={{display: 'flex', gap: '10px', mb:1}}>
                                        {quiz?.patient_photo !== null && (
                                            <img src={quiz?.patient_photo} alt={quiz?.title} width='400px'/>
                                        )}
                                        <input
                                            accept="image/*"
                                            type="file"
                                            onChange={handlePatPhotoUpload}
                                            style={{display: 'none'}}
                                            id="patient-upload"
                                        />
                                        <label htmlFor="patient-upload">
                                            <Button variant="contained" className='profileBtn' component="span"
                                                    sx={{mb: 2, background: '#3a485b'}}>
                                                Загрузить фото
                                            </Button>
                                        </label>
                                    </Box>

                                    <EditorToggle
                                        width="1000px"
                                        label="Диагноз"
                                        initialValue={patientContent}
                                        onChange={handlePatientChange}
                                    />
                                </Box>
                            )}

                            {activeTab === 'complaints' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.complaint_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, complaint_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={1}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={1} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={1}
                                        isBefore={false}
                                    />
                                </Box>
                            )}

                            {activeTab === 'anamnesis' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.anamnesis_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, anamnesis_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={2}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={2} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={2}
                                        isBefore={false}
                                    />
                                </Box>
                            )}
                            {activeTab === 'preliminaryDiagnosis' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.preliminary_diagnosis_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, preliminary_diagnosis_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={3}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={3} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={3}
                                        isBefore={false}
                                    />

                                </Box>
                            )}
                            {activeTab === 'research' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.research_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, research_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={4}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={4} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={4}
                                        isBefore={false}
                                    />
                                </Box>
                            )}
                            {activeTab === 'finalDiagnosis' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.final_diagnosis_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, final_diagnosis_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={5}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={5} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={5}
                                        isBefore={false}
                                    />
                                </Box>
                            )}
                            {activeTab === 'treatment' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.treatment_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, treatment_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={6}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={6} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={6}
                                        isBefore={false}
                                    />
                                </Box>
                            )}
                            {activeTab === 'conclusions' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Название панели"
                                        type="text"
                                        value={quiz?.conclusions_text}
                                        sx={{width: '1000px'}}
                                        onChange={(e) => setQuiz({...quiz!, conclusions_text: e.target.value})}
                                    />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={7}
                                        isBefore={true}
                                    />

                                    <QuestionEditor quizId={quizId!} section={7} />

                                    <MediaBlockEditor
                                        quizId={quizId!}
                                        section={7}
                                        isBefore={false}
                                    />
                                </Box>
                            )}
                            {activeTab === 'result' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>

                                    <EditorToggle
                                        width="700px"
                                        label="Результат"
                                        initialValue={resultContent}
                                        onChange={handleResultChange}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </>
                :
                <>
                    <Box className='notFoundElements'>
                        <CircularProgress/>
                    </Box>
                </>
            }


        </Box>

    );
};

export default Quiz1Edit;
